// ============================================================================
// TrimaCadranSelection — Multi-chip selection screen for a cadran
// ============================================================================
// Behavior:
//   - Items are shuffled on mount (per the brief: "triées aléatoirement")
//   - Tap a chip → toggle selection
//   - Counter shown only during refinement pass (x / min)
//   - Validate button always enabled; shows error message if min not reached
//   - On validate → parent handles FE refinement or BE POST
//
// The title, subtitle and instruction are rendered by TrimaCadranMain in a
// sticky header above this component.
// ============================================================================

import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/common/button/Button';
import TrimaCadranChip from 'pages/questionnaire-trima/slides/cadran/TrimaCadranChip';
import {
  TrimaCadranSelectionWrapper,
  TrimaCadranFooter,
} from 'pages/questionnaire-trima/slides/cadran/TrimaCadranSelection.styled';
import { TrimaCadranItem } from 'pages/questionnaire-trima/types/trima.types';

export interface TrimaCadranSelectionProps {
  items: TrimaCadranItem[];
  minSelections: number;
  /** True during a refinement pass — caps selection at minSelections. */
  isRefinement?: boolean;
  /** Pre-selected items, e.g. when restoring from localStorage. */
  initialSelections?: number[];
  /** Fired on every selection change (used by parent to persist). */
  onSelectionsChange?: (selectedIds: number[]) => void;
  /** Fired when user validates. */
  onValidate: (selectedIds: number[]) => void;
}

const shuffle = <T,>(input: T[]): T[] => {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const TrimaCadranSelection: React.FC<TrimaCadranSelectionProps> = ({
  items,
  minSelections,
  isRefinement,
  initialSelections,
  onSelectionsChange,
  onValidate,
}) => {
  const { t } = useTranslation('trima');

  const shuffledItems = useMemo(() => shuffle(items), [items]);

  const [selectedIds, setSelectedIds] = useState<number[]>(initialSelections ?? []);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!initialSelections) {
      setSelectedIds([]);
    }
    // eslint-disable
  }, [items]);

  const handleToggle = (itemId: number) => {
    setSelectedIds((prev) => {
      const isAdding = !prev.includes(itemId);
      // During refinement, block adding once the minimum is reached.
      if (isAdding && isRefinement && prev.length >= minSelections) return prev;
      const next = isAdding ? [...prev, itemId] : prev.filter((id) => id !== itemId);
      onSelectionsChange?.(next);
      return next;
    });
    setErrorMessage('');
  };

  const isValid = isRefinement
    ? selectedIds.length === minSelections
    : selectedIds.length >= minSelections;
  const isAtRefinementLimit = !!isRefinement && selectedIds.length >= minSelections;

  const handleValidateClick = () => {
    if (!isValid) {
      const key = isRefinement ? 'cadran.error-refine' : 'cadran.error-not-enough';
      setErrorMessage(t(key, { min: minSelections }));
      return;
    }
    setErrorMessage('');
    onValidate(selectedIds);
  };

  return (
    <TrimaCadranSelectionWrapper>
      <div className="trima-chips-grid">
        {shuffledItems.map((item) => (
          <TrimaCadranChip
            key={item.id}
            item={item}
            selected={selectedIds.includes(item.id)}
            disabled={isAtRefinementLimit && !selectedIds.includes(item.id)}
            onToggle={handleToggle}
          />
        ))}
      </div>

      <TrimaCadranFooter $active={isValid}>
        {isRefinement && (
          <span className="trima-counter">
            {t('cadran.selected-counter', {
              count: selectedIds.length,
              min: minSelections,
            })}
          </span>
        )}
        {errorMessage && (
          <span className="trima-error">{errorMessage}</span>
        )}
        <div className="trima-validate-row">
          <Button
            fill="solid"
            color="accent"
            expand
            onClick={handleValidateClick}
          >
            <span>{t('cadran.validate')}</span>
          </Button>
        </div>
      </TrimaCadranFooter>
    </TrimaCadranSelectionWrapper>
  );
};

export default TrimaCadranSelection;
